import React, { useState, useEffect } from 'react';
import { Search, Filter, MapPin, Phone, Home, Calendar, Trash2 } from 'lucide-react';
import apiWithAuth from '../../services/apiWithAuth';
import { usePermissions } from '../../hooks/usePermissions';
import './userApp.css';

const DEFAULT_PROFILE_IMAGE = "https://img.freepik.com/premium-vector/no-photo-available-vector-icon-default-image-symbol-picture-coming-soon-web-site-mobile-app_87543-18055.jpg";

export default function AdminUsersPage() {
    const permissions = usePermissions('KD Users');
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [usersPerPage] = useState(10);

    // Delete (super admin only)
    const [deletePopup, setDeletePopup] = useState({ show: false, user: null });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Fetch users data on component mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await apiWithAuth.get('/user-app/users/admin');

                if (response.data && response.data.data) {
                    setUsers(response.data.data);
                    setFilteredUsers(response.data.data);
                } else {
                    setUsers([]);
                    setFilteredUsers([]);
                }
            } catch (err) {
                console.error('Error fetching users:', err);
                setError(err.response?.data?.message || err.message || 'Failed to fetch users');
                setUsers([]);
                setFilteredUsers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    // Filter users whenever filter states change
    useEffect(() => {
        if (!Array.isArray(users)) {
            setFilteredUsers([]);
            return;
        }

        let result = [...users];

        // Filter by search term (name or phone)
        if (searchTerm) {
            result = result.filter(user =>
                (user.fullName && user.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (user.phone && user.phone.includes(searchTerm))
            );
        }

        // Filter by status
        if (statusFilter) {
            result = result.filter(user => user.status === statusFilter);
        }

        // Filter by location (city or state)
        if (locationFilter) {
            result = result.filter(user => {
                // Check if user has any address matching the location filter
                return user.addresses && user.addresses.some(address =>
                    (address.city && address.city.toLowerCase().includes(locationFilter.toLowerCase())) ||
                    (address.state && address.state.toLowerCase().includes(locationFilter.toLowerCase()))
                );
            });
        }

        // Filter by date range (createdAt)
        if (dateFrom) {
            const fromStart = new Date(dateFrom);
            fromStart.setHours(0, 0, 0, 0);
            result = result.filter(user => {
                const created = user.createdAt ? new Date(user.createdAt) : null;
                return created && created >= fromStart;
            });
        }
        if (dateTo) {
            const toEnd = new Date(dateTo);
            toEnd.setHours(23, 59, 59, 999);
            result = result.filter(user => {
                const created = user.createdAt ? new Date(user.createdAt) : null;
                return created && created <= toEnd;
            });
        }

        setFilteredUsers(result);
    }, [users, searchTerm, statusFilter, locationFilter, dateFrom, dateTo]);

    // Get unique status values
    const statusOptions = ['PENDING', 'ACTIVE', 'BLOCKED'];

    // Get unique cities from all user addresses
    const getUniqueLocations = () => {
        const locations = new Set();

        if (Array.isArray(users)) {
            users.forEach(user => {
                if (user.addresses) {
                    user.addresses.forEach(address => {
                        if (address.city) locations.add(address.city);
                        if (address.state) locations.add(address.state);
                    });
                }
            });
        }

        return Array.from(locations).sort();
    };

    // Delete user (super admin only - guarded by permissions.canDelete)
    const showDeletePopup = (user) => setDeletePopup({ show: true, user });
    const closeDeletePopup = () => setDeletePopup({ show: false, user: null });

    const handleDeleteUser = async () => {
        const { user } = deletePopup;
        if (!user) return;
        try {
            setDeleteLoading(true);
            await apiWithAuth.delete(`/users/single-profile/hard?userId=${user._id}`);
            setUsers(prev => prev.filter(u => u._id !== user._id));
            setFilteredUsers(prev => prev.filter(u => u._id !== user._id));
            closeDeletePopup();
        } catch (err) {
            console.error('Error deleting user:', err);
            alert(err.response?.data?.message || err.message || 'Failed to delete user');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Handle image loading error
    const handleImageError = (e) => {
        e.target.src = DEFAULT_PROFILE_IMAGE;
        e.target.onerror = null; // Prevent infinite loop
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'ACTIVE': return 'status-badge status-active';
            case 'PENDING': return 'status-badge status-pending';
            case 'BLOCKED': return 'status-badge status-blocked';
            // case 'DELETED': return 'status-badge status-deleted';
            default: return 'status-badge status-default';
        }
    };

    // Format date with fallback
    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch (error) {
            return 'Invalid date';
        }
    };

    // Format datetime with fallback
    const formatDateTime = (dateString) => {
        if (!dateString) return 'Never';
        try {
            return new Date(dateString).toLocaleString();
        } catch (error) {
            return 'Invalid date';
        }
    };

    // Get current users for pagination
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = Array.isArray(filteredUsers) ? filteredUsers.slice(indexOfFirstUser, indexOfLastUser) : [];
    const totalPages = Math.ceil((filteredUsers?.length || 0) / usersPerPage);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Go to next page
    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Go to previous page
    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">Loading users data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <h3>Error Loading Users</h3>
                <p>{error}</p>
                <button
                    className="retry-button"
                    onClick={() => window.location.reload()}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="admin-users-container">
            <h1 className="page-title">User Management</h1>

            {/* Filters and Search */}
            <div className="filters-container-admin">
                <div className="filters-grid">
                    {/* Search Input */}
                    <div className="search-container">
                        <div className="search-icon-container">
                            <Search size={18} />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by name or phone"
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Status Filter */}
                    <div className="filter-container">
                        <div className="filter-label">
                            <Filter size={16} />
                            <span>Filter by Status</span>
                        </div>
                        <select
                            className="select-input"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            {statusOptions.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>

                    {/* Location Filter */}
                    <div className="filter-container">
                        <div className="filter-label">
                            <MapPin size={16} />
                            <span>Filter by Location</span>
                        </div>
                        <select
                            className="select-input"
                            value={locationFilter}
                            onChange={(e) => setLocationFilter(e.target.value)}
                        >
                            <option value="">All Locations</option>
                            {getUniqueLocations().map(location => (
                                <option key={location} value={location}>{location}</option>
                            ))}
                        </select>
                    </div>

                    {/* Date Filter */}
                    <div className="filter-container">
                        <div className="filter-label">
                            <Calendar size={16} />
                            <span>Filter by Date</span>
                        </div>
                        <div className="date-filter-inputs">
                            <input
                                type="date"
                                className="date-input"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                title="From date"
                            />
                            <span className="date-separator">to</span>
                            <input
                                type="date"
                                className="date-input"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                title="To date"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Users Count */}
            <div className="users-count">
                Showing {filteredUsers?.length || 0} of {users?.length || 0} users
            </div>

            {/* Users Table */}
            {!filteredUsers || filteredUsers.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">🔍</div>
                    <p className="empty-state-text">No users match your search criteria</p>
                    <button
                        className="reset-filters-button"
                        onClick={() => {
                            setSearchTerm('');
                            setStatusFilter('');
                            setLocationFilter('');
                            setDateFrom('');
                            setDateTo('');
                        }}
                    >
                        Reset Filters
                    </button>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Contact</th>
                                <th>Status</th>
                                <th>Addresses</th>
                                {permissions.canDelete && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {currentUsers.map(user => (
                                <tr key={user._id}>
                                    <td>
                                        <div className="user-profile-admin">
                                            <div className="profile-image">
                                                {user.profileImageUrl ? (
                                                    <img
                                                        src={DEFAULT_PROFILE_IMAGE}
                                                        alt={user.fullName || 'User'}
                                                        onError={handleImageError}
                                                    />
                                                ) : (
                                                    <img
                                                        src={DEFAULT_PROFILE_IMAGE}
                                                        alt={user.fullName || 'User'}
                                                        className="default-profile-image"
                                                    />
                                                )}
                                            </div>
                                            <div className="user-info">
                                                <div className="user-name">
                                                    {user.fullName || 'No Name'}
                                                </div>
                                                <div className="user-id">
                                                    ID: {user._id.substring(0, 8)}...
                                                </div>
                                                <div className="user-created-date">
                                                    Created: {formatDate(user.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="contact-info">
                                            <div className="phone-info">
                                                <Phone size={14} className="contact-icon" />
                                                <span>{user.phone || 'No Phone'}</span>
                                                {user.phoneVerified && (
                                                    <span className="verification-badge">
                                                        Verified
                                                    </span>
                                                )}
                                            </div>
                                            {user.email && (
                                                <div className="email-info">
                                                    <span className="email-text">{user.email}</span>
                                                    {user.emailVerified && (
                                                        <span className="verification-badge">
                                                            Verified
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={getStatusBadgeClass(user.status)}>
                                            {user.status}
                                        </span>
                                        <div className="last-login">
                                            Last login: {formatDateTime(user.lastLogin)}
                                        </div>
                                    </td>
                                    <td>
                                        {user.addresses && user.addresses.length > 0 ? (
                                            <div className="addresses-container">
                                                {user.addresses.map((address, index) => (
                                                    <div key={address._id || index} className="address-card">
                                                        <div className="address-header">
                                                            <span className="address-type">{address.type || 'Address'}</span>
                                                            <Home size={14} className="address-icon" />
                                                        </div>
                                                        <div className="address-location">
                                                            {address.city}{address.state ? `, ${address.state}` : ''}
                                                        </div>
                                                        <div className="address-details">
                                                            {address.buildingNo || ''} {address.street || ''}
                                                            {address.pincode ? `, ${address.pincode}` : ''}
                                                        </div>
                                                    </div>
                                                ))}
                                                {user.addresses.length > 1 && (
                                                    <div className="address-count">
                                                        {user.addresses.length} addresses total
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="no-data-admin">No addresses</span>
                                        )}
                                    </td>
                                    {permissions.canDelete && (
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    type="button"
                                                    className="action-button delete-button"
                                                    onClick={() => showDeletePopup(user)}
                                                    title="Delete user"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                onClick={prevPage}
                                disabled={currentPage === 1}
                                className="pagination-button"
                            >
                                Previous
                            </button>

                            <div className="pagination-numbers">
                                {[...Array(totalPages).keys()].map(number => (
                                    <button
                                        key={number + 1}
                                        onClick={() => paginate(number + 1)}
                                        className={`pagination-button ${currentPage === number + 1 ? 'active' : ''}`}
                                    >
                                        {number + 1}
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={nextPage}
                                disabled={currentPage === totalPages}
                                className="pagination-button"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Delete confirmation modal (super admin only) */}
            {deletePopup.show && (
                <div className="user-app-delete-overlay" onClick={closeDeletePopup}>
                    <div className="user-app-delete-popup" onClick={e => e.stopPropagation()}>
                        <div className="user-app-delete-header">
                            <Trash2 size={24} className="user-app-delete-icon" />
                            <h3>Delete User</h3>
                        </div>
                        <div className="user-app-delete-body">
                            <p>Are you sure you want to delete this user?</p>
                            <div className="user-app-delete-info">
                                <div className="user-app-delete-avatar">
                                    {deletePopup.user?.fullName?.charAt(0).toUpperCase() || '?'}
                                </div>
                                <div>
                                    <div className="user-app-delete-name">{deletePopup.user?.fullName || 'No name'}</div>
                                    <div className="user-app-delete-meta">{deletePopup.user?.phone || deletePopup.user?.email || '—'}</div>
                                </div>
                            </div>
                            <p className="user-app-delete-warning">This action cannot be undone.</p>
                        </div>
                        <div className="user-app-delete-footer">
                            <button type="button" onClick={closeDeletePopup} className="user-app-delete-cancel">
                                Cancel
                            </button>
                            <button type="button" onClick={handleDeleteUser} className="user-app-delete-confirm" disabled={deleteLoading}>
                                {deleteLoading ? 'Deleting...' : 'Delete User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Users summary */}
            {/* {filteredUsers.length > 0 && (
                <div className="users-summary">
                    <p>
                        Showing page {currentPage} of {totalPages} •
                        {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
                    </p>
                </div>
            )} */}
        </div>
    );
}